import type { MaybeRef } from '@vueuse/core'

type Time = `>${number}` | `<${number}` | `>=${number}` | `<=${number}`

type DropZoneTimeActions = Record<Time, () => void>

export interface useDragHoverArgs {
  node?: (Ref<HTMLElement | undefined | null>) | (ComputedRef<HTMLElement | undefined | null>)
  disabled?: MaybeRef<boolean>
  timeActions?: DropZoneTimeActions
}

function matchTime(time: Time, value: number) {
  const timeValue = Number(time.replace(/\D/, ''))
  const timeOperator = time.replace(timeValue.toString(), '')
  switch (timeOperator) {
    case '>':
      return value > timeValue
    case '<':
      return value < timeValue
    case '>=':
      return value >= timeValue
    case '<=':
      return value <= timeValue
  }
}

interface DragData<T> { index: number; data: T }

export const useDragHover = <T>({ node, disabled, timeActions }: useDragHoverArgs) => {
  const [isDragHover, setDragHover] = useToggle()
  const isDragHoverDebounced = useDebounce(isDragHover, 100)

  const zoneId = randomID()
  const timeStart = ref(0)
  const hoverTime = ref(0)
  const keysDispatchTimeAction = ref<Time[]>([])
  const setTimeStart = () => {
    timeStart.value = Date.now()
  }

  const hoverElementData = ref<DragData<T> | null>(null)

  watch(hoverElementData, (element) => {
    if (element)
      useDispatchCustomEvent('dropzone:dragenter', { id: zoneId, data: hoverElementData.value })
  })

  const dispachTimeAction = () => {
    if (!timeActions)
      return
    const currentTime = hoverTime.value
    for (const key in timeActions) {
      if (matchTime(key as Time, currentTime) && !keysDispatchTimeAction.value.includes(key as Time)) {
        timeActions[key as Time]()
        keysDispatchTimeAction.value.push(key as Time)
      }
    }
  }

  const updateHoverTime = () => {
    const now = Date.now()
    hoverTime.value = now - timeStart.value
    dispachTimeAction()
  }

  const resetHoverTime = () => {
    timeStart.value = Date.now()
    hoverTime.value = 0
    keysDispatchTimeAction.value = []
  }
  const handlerDragOver = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()

    updateHoverTime()
    setDragHover(true)
  }

  const reset = () => {
    hoverElementData.value = null
    setDragHover(false)
    resetHoverTime()
  }

  const validataLeave = (event: DragEvent) => {
    const rect = node?.value?.getBoundingClientRect()
    if (!rect)
      return true

    const isInside = event.clientX >= rect?.left && event.clientX <= rect?.right && event.clientY >= rect?.top && event.clientY <= rect?.bottom
    return !isInside
  }

  const handlerDragLeave = (event: DragEvent) => {
    setDragHover(false)
    if (!validataLeave(event))
      return
    useDispatchCustomEvent('dropzone:dragleave', { id: zoneId, data: hoverElementData.value })
    reset()
  }

  const handlerDragEnter = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setTimeStart()
    setDragHover(true)
  }

  const addEventListener = () => {
    const el = node?.value
    if (!el || unref(disabled))
      return
    el.addEventListener('dragover', handlerDragOver)
    el.addEventListener('dragenter', handlerDragEnter)
    el.addEventListener('dragleave', handlerDragLeave)
  }

  const clearEventListeners = () => {
    const el = node?.value
    if (!el)
      return
    el.removeEventListener('dragover', handlerDragOver)
    el.removeEventListener('dragenter', handlerDragEnter)
    el.removeEventListener('dragleave', handlerDragLeave)
  }

  watch(() => node?.value, (el) => {
    if (!el || unref(disabled))
      return
    // el.setAttribute(DRAG_ZONES_DATA_KEYS.DATA_ZONE_ID, zoneId)

    addEventListener()
  })

  watch(() => unref(disabled), (isDisabled) => {
    if (isDisabled)
      clearEventListeners()
  })

  return { isDragHover: isDragHoverDebounced, hoverElementData }
}
